import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Download,
    BookOpen,
    Wrench,
    CheckCircle2,
    AlertTriangle,
    ArrowRight,
} from "lucide-react";
import SEO, {
    organizationJsonLd,
    websiteJsonLd,
    personJsonLd,
    buildFaqJsonLd,
    buildBreadcrumbsJsonLd,
} from "@/components/common/SEO";
import { SITE_URL } from "@/lib/api";
import { LIBRO, CUENTOS } from "@/data/staticContent";

/**
 * SeoLanding — layout reutilizable para landings SEO 1000+ palabras
 * con estilo brutalist noir del sitio. Props:
 *   config: { slug, seoTitle, seoDescription, seoKeywords, heroImage,
 *             eyebrow, h1Prefix, h1Highlight, h1Suffix, kicker,
 *             sections: [{h2, body, list?}], faqs: [{q,a}], crumbLabel }
 */
export default function SeoLanding({ config }) {
    const canonical = `${SITE_URL}/${config.slug}`;
    const bookAlso = {
        "@context": "https://schema.org",
        "@type": "Book",
        name: LIBRO.title,
        url: `${SITE_URL}/libro`,
        author: {
            "@type": "Person",
            name: "Nelson González Rodríguez",
            url: `${SITE_URL}/nelson-gonzalez-rodriguez`,
        },
        inLanguage: "es",
        bookFormat: "https://schema.org/EBook",
        isAccessibleForFree: true,
    };
    const collection = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": `${canonical}#collection`,
        url: canonical,
        name: config.seoTitle,
        description: config.seoDescription,
        inLanguage: "es",
        isPartOf: { "@id": `${SITE_URL}/#website` },
        about: (config.about || []).map((t) => ({ "@type": "Thing", name: t })),
        mainEntity: {
            "@type": "ItemList",
            numberOfItems: 2,
            itemListElement: [
                { "@type": "ListItem", position: 1, item: bookAlso },
                {
                    "@type": "ListItem",
                    position: 2,
                    item: {
                        "@type": "CreativeWorkSeries",
                        name: "Cuentos para adictos",
                        url: "https://www.sinadicciones.org/cuentos-para-adictos",
                        numberOfItems: 4,
                        author: {
                            "@type": "Person",
                            name: "Nelson González Rodríguez",
                            url: `${SITE_URL}/nelson-gonzalez-rodriguez`,
                        },
                    },
                },
            ],
        },
    };
    const breadcrumbs = buildBreadcrumbsJsonLd([
        { name: "Inicio", url: SITE_URL + "/" },
        { name: config.crumbLabel, url: canonical },
    ]);
    const jsonLd = [
        collection,
        breadcrumbs,
        buildFaqJsonLd(config.faqs),
        personJsonLd,
        organizationJsonLd,
        websiteJsonLd,
    ];

    return (
        <>
            <SEO
                title={config.seoTitle}
                description={config.seoDescription}
                path={`/${config.slug}`}
                image={config.heroImage}
                keywords={config.seoKeywords}
                jsonLd={jsonLd}
            />

            {/* Hero */}
            <section
                className="relative grain border-b-2 border-[var(--danger)]"
                data-testid={`landing-hero-${config.slug}`}
            >
                <div className="hazard-stripes h-2 w-full" />
                <div className="container-x py-16 md:py-24 max-w-5xl">
                    <p className="eyebrow mb-4">{config.eyebrow}</p>
                    <h1
                        className="font-display text-5xl md:text-7xl lg:text-[5rem] leading-[0.9] tracking-tight mb-8"
                        data-testid="landing-h1"
                    >
                        {config.h1Prefix}{" "}
                        <span className="text-[var(--danger)]">
                            {config.h1Highlight}
                        </span>{" "}
                        {config.h1Suffix}
                    </h1>
                    <p className="font-mono text-lg md:text-xl text-[var(--paper-2)] leading-relaxed max-w-3xl mb-8">
                        {config.kicker}
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Link
                            to="/descargar"
                            className="btn-brutal btn-brutal-danger"
                            data-testid="landing-cta-descargar"
                        >
                            <Download className="w-4 h-4" />
                            Descargar libro gratis
                        </Link>
                        <Link
                            to="/libro"
                            className="btn-brutal btn-brutal-ghost"
                            data-testid="landing-cta-libro"
                        >
                            <BookOpen className="w-4 h-4" />
                            Ver el libro completo
                        </Link>
                    </div>
                </div>
            </section>

            {/* Cuerpo — secciones */}
            <section className="py-16 md:py-24">
                <div className="container-x max-w-3xl prose-dark space-y-8">
                    {config.sections.map((s, i) => (
                        <div key={i} data-testid={`landing-section-${i}`}>
                            <h2 className="font-display text-3xl md:text-4xl text-[var(--paper-2)] leading-tight mb-4">
                                {s.h2}
                            </h2>
                            {s.body.map((p, j) => (
                                <p key={j}>{p}</p>
                            ))}
                            {s.list && (
                                <ul className="my-5 space-y-3">
                                    {s.list.map((li, k) => (
                                        <li
                                            key={k}
                                            className="font-mono text-sm md:text-base text-[var(--paper)] flex gap-3 leading-relaxed"
                                        >
                                            <ArrowRight className="w-4 h-4 text-[var(--danger)] flex-shrink-0 mt-1" />
                                            <span>{li}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* Bloque libros — obligatorio en todas las landings */}
            <section
                className="py-16 md:py-20 bg-[#0a0a0a] border-y border-[var(--line)]"
                data-testid="landing-obras"
            >
                <div className="container-x max-w-5xl">
                    <p className="eyebrow mb-4">Obras de Nelson · verificadas</p>
                    <h2 className="font-display text-3xl md:text-4xl mb-10 leading-tight">
                        {config.obrasHeadline || "Lo que puedes leer ya mismo."}
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="border-2 border-[var(--paper)] bg-[#0d0d0d] p-6 md:p-8">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)] mb-3">
                                Libro digital · 141 pág · PDF + EPUB
                            </p>
                            <h3 className="font-display text-3xl text-[var(--paper-2)] mb-3 leading-tight">
                                El Principio del Mapa Inverso
                            </h3>
                            <p className="font-mono text-sm text-[var(--paper)] leading-relaxed mb-6">
                                16 capítulos crudos sobre adicción, recaídas,
                                centros de rehabilitación, familia y
                                reconstrucción. Escrito por{" "}
                                <Link
                                    to="/nelson-gonzalez-rodriguez"
                                    className="underline text-[var(--paper-2)] hover:text-[var(--warning)]"
                                >
                                    Nelson González Rodríguez
                                </Link>{" "}
                                desde su propia recuperación.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <Link
                                    to="/descargar"
                                    className="btn-brutal btn-brutal-danger"
                                >
                                    <Download className="w-4 h-4" />
                                    Descargar gratis
                                </Link>
                                <Link to="/libro" className="btn-brutal">
                                    Ver ficha
                                    <ArrowUpRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                        <div className="border-2 border-[var(--warning)] bg-[#0d0d0d] p-6 md:p-8">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)] mb-3">
                                Serie literaria · 4 cuentos · lectura online
                            </p>
                            <h3 className="font-display text-3xl text-[var(--paper-2)] mb-3 leading-tight">
                                Cuentos para adictos
                            </h3>
                            <p className="font-mono text-sm text-[var(--paper)] leading-relaxed mb-6">
                                Cuatro cuentos breves que toman frases célebres
                                de los grupos de recuperación —los dos lobos,
                                quemar las naves, evita personas/lugares/cosas,
                                Papá ¿qué es un adicto?— y las continúan.
                                Publicados en SinAdicciones.org.
                            </p>
                            <div className="flex flex-col gap-2">
                                {CUENTOS.map((c) => (
                                    <a
                                        key={c.slug}
                                        href={c.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-mono text-xs text-[var(--paper-2)] hover:text-[var(--warning)] inline-flex items-center gap-2"
                                    >
                                        <ArrowUpRight className="w-3.5 h-3.5 text-[var(--warning)]" />
                                        {c.title}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Disclaimer clínico */}
            <section className="py-10 border-y border-[var(--danger)] bg-[#0d0d0d]">
                <div className="container-x max-w-3xl">
                    <div className="flex gap-4 items-start">
                        <AlertTriangle className="w-6 h-6 text-[var(--danger)] flex-shrink-0 mt-0.5" />
                        <p className="font-mono text-xs md:text-sm text-[var(--paper)] leading-relaxed">
                            <strong className="text-[var(--danger)]">
                                Aviso responsable:
                            </strong>{" "}
                            este libro y las páginas de este sitio contienen
                            textos personales, irónicos y testimoniales sobre
                            adicción, recaídas y recuperación. No reemplazan
                            evaluación médica, terapia psicológica, psiquiatría,
                            tratamiento profesional ni ayuda de emergencia. Si
                            estás en riesgo, no puedes parar o tienes ideas de
                            hacerte daño, busca ayuda profesional inmediata o
                            acude a un servicio de urgencia (SENDA 1412 en
                            Chile).
                        </p>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20 md:py-28" data-testid="landing-faqs">
                <div className="container-x max-w-3xl">
                    <p className="eyebrow mb-4">Preguntas frecuentes</p>
                    <h2 className="font-display text-4xl md:text-5xl mb-12 leading-[0.95]">
                        Lo que la gente pregunta.
                    </h2>
                    <ol className="space-y-6">
                        {config.faqs.map((f, i) => (
                            <li
                                key={i}
                                className="border-l-2 border-[var(--danger)] pl-6"
                                data-testid={`landing-faq-${i}`}
                            >
                                <h3 className="font-display text-xl md:text-2xl text-[var(--paper-2)] mb-2">
                                    {f.q}
                                </h3>
                                <p className="font-mono text-sm text-[var(--paper)] leading-relaxed">
                                    {f.a}
                                </p>
                            </li>
                        ))}
                    </ol>

                    <div className="mt-16 pt-10 border-t border-dashed border-[var(--ink-dim)] text-center">
                        <p className="font-hand text-3xl text-[var(--warning)] -rotate-1 inline-block mb-6">
                            ¿Todavía dudas?
                        </p>
                        <br />
                        <Link
                            to="/descargar"
                            className="btn-brutal btn-brutal-danger inline-flex"
                            data-testid="landing-final-cta"
                        >
                            <Download className="w-4 h-4" />
                            Descargar el libro gratis
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
